import {
  BRAND_BRAIN_FIELD_ORDER,
  BRAND_BRAIN_SECTIONS,
  brandBrainFieldKey,
  isBrandBrainValueGap,
  type BrandBrainFieldSource,
  type BrandBrainFieldState,
  type BrandBrainReadModel,
  type BrandBrainSectionId,
} from "@nexus/contracts";

export interface BrandBrainRow {
  id: string;
  source: BrandBrainFieldSource;
  column: string;
  section: BrandBrainSectionId;
  value: unknown;
  isGap: boolean;
  /** Onboarding fields the update route accepts and the Nexus-owned fields; Manus-enriched fields never are. */
  editable: boolean;
}

export interface BrandBrainSectionView {
  id: BrandBrainSectionId;
  /** Design numbering (`01`, `02`, …), fixed by section order so it never shifts when a section is empty. */
  number: string;
  /** Rows shown under the current filter; empty for a complete section under Gaps only. */
  rows: BrandBrainRow[];
  fieldCount: number;
  gapCount: number;
}

export interface BrandBrainSummary {
  total: number;
  collected: number;
  notCollected: number;
}

/** Generated fields have no place in the field order and keep their own order after the rest. */
const UNORDERED = BRAND_BRAIN_FIELD_ORDER.length;

/**
 * `canEditNexus`: the Nexus-owned fields are written by the Adge team only (the update route
 * refuses them from customer organization roles), so for anyone else they read as locked.
 */
export function brandBrainRows(model: BrandBrainReadModel, { canEditNexus = false } = {}): BrandBrainRow[] {
  const rows = [
    ...rowsFrom("clientContext", model.clientContext.fields),
    ...rowsFrom("nexus", model.nexus?.fields ?? {}).map((row) => (canEditNexus ? row : { ...row, editable: false })),
    ...rowsFrom("enriched", model.enriched.fields),
  ];
  const position = (row: BrandBrainRow) =>
    row.source === "enriched" ? UNORDERED : (BRAND_BRAIN_FIELD_ORDER.indexOf(row.column) + 1 || UNORDERED);
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => position(a.row) - position(b.row) || a.index - b.index)
    .map(({ row }) => row);
}

export function brandBrainSummary(rows: readonly BrandBrainRow[]): BrandBrainSummary {
  const notCollected = rows.filter((row) => row.isGap).length;
  return { total: rows.length, collected: rows.length - notCollected, notCollected };
}

/**
 * Sections follow the design order. A section with no confirmed fields is omitted.
 * Gaps only drops every collected row and every section that has no gaps left.
 */
export function brandBrainSections(
  rows: readonly BrandBrainRow[],
  gapsOnly: boolean,
): BrandBrainSectionView[] {
  return BRAND_BRAIN_SECTIONS.flatMap((id, index) => {
    const sectionRows = rows.filter((row) => row.section === id);
    if (sectionRows.length === 0) return [];
    const gapRows = sectionRows.filter((row) => row.isGap);
    if (gapsOnly && gapRows.length === 0) return [];
    return [{
      id,
      number: String(index + 1).padStart(2, "0"),
      rows: gapsOnly ? gapRows : sectionRows,
      fieldCount: sectionRows.length,
      gapCount: gapRows.length,
    }];
  });
}

/** String lists use the design separator. Other JSON is shown as stored, not reshaped. */
export function formatBrandBrainValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value.join(" · ");
  }
  if (value === null || value === undefined) return "";
  return JSON.stringify(value);
}

function rowsFrom(
  source: BrandBrainFieldSource,
  fields: Record<string, BrandBrainFieldState>,
): BrandBrainRow[] {
  return Object.entries(fields).flatMap(([column, state]) => {
    if (!state.visible) return [];
    return [{
      id: brandBrainFieldKey(source, column),
      source,
      column,
      section: state.section,
      value: state.value,
      isGap: state.isGap || isBrandBrainValueGap(state.value),
      editable: source !== "enriched" && state.editable,
    }];
  });
}

/** The stored values of the editable fields (onboarding and Nexus-owned), keyed by column, to seed an edit draft. */
export function brandBrainEditableValues(model: BrandBrainReadModel): Record<string, unknown> {
  return Object.fromEntries(
    [...Object.entries(model.clientContext.fields), ...Object.entries(model.nexus?.fields ?? {})]
      .filter(([, state]) => state.visible && state.editable)
      .map(([column, state]) => [column, state.value ?? null]),
  );
}
